import { ElementType } from 'react';
import { Box, Card, CardContent, Grid, Typography } from '@mui/material';
import {
    SmsOutlined,
    EmailOutlined,
    WalletOutlined,
    DesktopWindowsOutlined,
    PeopleAlt as PeopleAltIcon,
} from '@mui/icons-material';

const CHANNEL_CONFIG: Record<string, { label: string; icon: ElementType; color: string }> = {
    sms:      { label: 'SMS',       icon: SmsOutlined,            color: '#FF6B6B' },
    email:    { label: 'Email',     icon: EmailOutlined,          color: '#4ECDC4' },
    wallet:   { label: 'Wallet',    icon: WalletOutlined,         color: '#45B7D1' },
    cashdesk: { label: 'Cash Desk', icon: DesktopWindowsOutlined, color: '#F7931E' },
    courrier: { label: 'Courrier',  icon: DesktopWindowsOutlined, color: '#9C6FDE' },
};

const ALWAYS_VISIBLE_CHANNELS = ['wallet'];

const normalizeChannel = (channel: string) => channel.trim().toLowerCase();

const getChannelConfig = (channel: string) => {
    const key = Object.keys(CHANNEL_CONFIG).find(k => k.toLowerCase() === channel.toLowerCase());
    return key ? CHANNEL_CONFIG[key] : { label: channel, icon: PeopleAltIcon, color: '#7986cb' };
};

interface CountCardsProps {
    listCount: Array<{ channel: string; count: number }>;
}

function CountCards({ listCount }: CountCardsProps) {
    const displayListCount = [
        ...listCount,
        ...ALWAYS_VISIBLE_CHANNELS
            .filter(channel => !listCount.some(item => normalizeChannel(item.channel) === channel))
            .map(channel => ({ channel, count: 0 })),
    ];
    const total = displayListCount.reduce((acc, item) => acc + item.count, 0);

    return (
        <Grid container spacing={2}>
            {displayListCount.map((item) => {
                const cfg = getChannelConfig(item.channel);
                const IconComponent = cfg.icon;
                const percentage = total > 0 ? Math.round((item.count / total) * 100) : 0;

                return (
                    <Grid size={{ xs: 12, sm: 6, md: 3 }} key={item.channel}>
                        <Card elevation={0} sx={{ border: `2px solid ${cfg.color}`, borderRadius: 2, height: '120px', overflow: 'hidden' }}>
                            <CardContent sx={{ p: 2, height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'space-between', '&:last-child': { pb: 2 } }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Box sx={{ p: 0.8, borderRadius: 1.5, backgroundColor: `${cfg.color}18`, display: 'flex' }}>
                                        <IconComponent sx={{ fontSize: 20, color: cfg.color }} />
                                    </Box>
                                    <Box>
                                        <Typography variant="caption" sx={{ color: '#6c757d', fontSize: '0.7rem' }}>
                                            {cfg.label}
                                        </Typography>
                                        <Typography variant="caption" sx={{ color: cfg.color, fontWeight: 700, fontSize: '0.7rem', display: 'block' }}>
                                            {percentage}% du total
                                        </Typography>
                                    </Box>
                                </Box>
                                <Typography variant="h4" sx={{ fontWeight: 800, color: cfg.color, fontSize: '1.8rem', lineHeight: 1 }}>
                                    {item.count.toLocaleString('fr-FR')}
                                    <Typography component="span" variant="caption" sx={{ color: '#adb5bd', ml: 0.5, fontSize: '0.7rem' }}>
                                        clients
                                    </Typography>
                                </Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                );
            })}
        </Grid>
    );
}

export default CountCards;
